// Browser client for the agent runtime (POST /api/agent/chat, SSE).
// Contract: see ./uiTools.js (AGENT_EVENTS). For every `ui_action` event the browser runs the
// action via runUiAction(name, input) and ALWAYS posts the outcome to
// POST /api/agent/ui-result/:call_id — even if the action throws — so the server never stalls.
//
// If /api/agent/chat is not deployed (404/405) we fall back to the legacy streamChatResponse
// path (/api/chat/stream → /api/chat) and synthesize delta/done events.
import { useCallback, useRef, useState } from "react";
import { AGENT_EVENTS } from "./uiTools.js";
import { streamChatResponse, visibleChatText } from "../chatStreamClient.js";

function parseSseBlock(block) {
  let event = "message";
  const data = [];
  for (const line of block.split(/\r?\n/)) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
  }
  if (!data.length) return null;
  let payload;
  try { payload = JSON.parse(data.join("\n")); }
  catch { payload = { text: data.join("\n") }; }
  // Some servers put the event name inside the JSON payload instead of an `event:` line.
  if (event === "message" && payload && typeof payload.type === "string") event = payload.type;
  return { event, payload };
}

const _json = { "Content-Type": "application/json" };

export async function postUiResult(apiFetch, callId, body) {
  try {
    await apiFetch(`/api/agent/ui-result/${encodeURIComponent(callId)}`, {
      method: "POST", headers: _json, body: JSON.stringify(body), silent: true,
    });
  } catch (e) { console.warn("[agent] ui-result post failed:", e?.message); }
}

export async function confirmAgentCall(apiFetch, callId, approve) {
  const r = await apiFetch(`/api/agent/confirm/${encodeURIComponent(callId)}`, {
    method: "POST", headers: _json, body: JSON.stringify({ approve: !!approve }),
  });
  let data = null;
  try { data = await r.json(); } catch { data = null; }
  if (!r.ok) {
    const err = new Error((data && (data.error || data.message)) || `HTTP ${r.status}`);
    err.status = r.status; err.data = data;
    throw err;
  }
  return data || { ok: true };
}

// Cap a JSON-able value to ~maxChars when serialized (keeps ui-result payloads small).
export function capResult(value, maxChars = 4000) {
  let s;
  try { s = JSON.stringify(value); } catch { return { note: "unserializable result" }; }
  if (!s || s.length <= maxChars) return value;
  return { truncated: true, preview: s.slice(0, maxChars - 60) };
}

/**
 * Stream one agent turn.
 * @returns {Promise<{text:string, steps?:number, tools_used?:string[], ms?:number, fallback?:boolean}>}
 */
export async function streamAgentChat({ apiFetch, agent, messages, context, onEvent, runUiAction, signal, onFallback }) {
  const emit = (event, payload) => { try { onEvent?.(event, payload || {}); } catch (e) { console.warn("[agent] onEvent threw:", e); } };
  const body = JSON.stringify({ agent, messages, context });
  let response;
  try {
    response = await apiFetch("/api/agent/chat", {
      method: "POST",
      headers: { ..._json, Accept: "text/event-stream" },
      body, signal,
    });
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    response = null; // network failure → try fallback below
  }

  // ── Fallback: legacy stream (no tool loop) ──
  const _isHtml = !!response && /text\/html/i.test(response.headers.get("content-type") || "");
  if (!response || response.status === 404 || response.status === 405 || (response.ok && _isHtml)) {
    if (typeof onFallback === "function") {
      const r = await onFallback(response ? response.status : 0);
      return { ...(r && typeof r === "object" ? r : { text: String(r ?? "") }), fallback: true };
    }
    let full = "";
    const raw = await streamChatResponse(apiFetch, { model: "minimax-m3", max_tokens: 800, agent, context, messages }, {
      controller: signal ? { signal } : undefined,
      onAck: (p) => emit(AGENT_EVENTS.ACK, p),
      onDelta: (_chunk, fullText) => { full = fullText; emit("replace", { text: visibleChatText(fullText) }); },
    });
    const text = visibleChatText(String(raw ?? full ?? ""));
    const meta = raw && typeof raw.chatMeta === "function" ? raw.chatMeta() : (raw && raw.__chatMeta) || null;
    emit(AGENT_EVENTS.DONE, { text, fallback: true });
    return { text, fallback: true, legacyMeta: meta };
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    let msg = t;
    try { msg = JSON.parse(t)?.error || t; } catch { /* plain text */ }
    throw new Error(msg || `HTTP ${response.status}`);
  }

  const ctype = response.headers.get("content-type") || "";
  if (!ctype.includes("text/event-stream") || !response.body) {
    // Non-streamed JSON reply — treat as final.
    const d = await response.json().catch(() => ({}));
    const text = String(d?.text ?? d?.message ?? d?.choices?.[0]?.message?.content ?? "");
    emit(AGENT_EVENTS.DONE, { ...d, text });
    return { ...d, text };
  }

  const pendingUi = [];
  const handleUiAction = (p) => {
    const callId = p?.call_id;
    const job = (async () => {
      let out;
      try {
        if (typeof runUiAction !== "function") throw new Error("UI bridge unavailable in this view");
        const result = await runUiAction(p?.name, p?.input || {});
        if (result && result.ok === false) out = { ok: false, error: String(result.error || "failed") };
        else out = { ok: true, result: capResult(result ?? { done: true }) };
      } catch (e) {
        out = { ok: false, error: String(e?.message || e || "ui action failed") };
      }
      emit("ui_result", { call_id: callId, name: p?.name, ...out });
      if (callId) await postUiResult(apiFetch, callId, out);
    })();
    pendingUi.push(job);
  };

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullText = "";
  let donePayload = null;
  let errorPayload = null;
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const blocks = buffer.split(/\r?\n\r?\n/);
      buffer = blocks.pop() || "";
      for (const block of blocks) {
        const parsed = parseSseBlock(block);
        if (!parsed) continue;
        const { event, payload } = parsed;
        if (event === AGENT_EVENTS.DELTA) {
          const chunk = typeof payload?.text === "string" ? payload.text : "";
          fullText += chunk;
          emit(event, { text: chunk, full: fullText });
        } else if (event === AGENT_EVENTS.UI_ACTION) {
          emit(event, payload);
          handleUiAction(payload);
        } else if (event === AGENT_EVENTS.DONE) {
          donePayload = payload || {};
          emit(event, donePayload);
        } else if (event === AGENT_EVENTS.ERROR) {
          errorPayload = payload || {};
          emit(event, errorPayload);
        } else {
          emit(event, payload);
        }
      }
      if (done) break;
    }
  } finally {
    try { reader.releaseLock(); } catch { /* ignore */ }
    // Never leave a ui_action un-answered.
    await Promise.allSettled(pendingUi);
  }
  if (errorPayload && !donePayload) {
    const err = new Error(errorPayload.message || errorPayload.error || "Agent error");
    err.partial = fullText;
    throw err;
  }
  const text = String(donePayload?.text ?? fullText ?? "");
  return { ...(donePayload || {}), text };
}

/**
 * Reducer for an assistant chat message object while events stream in.
 * Adds fields: _agent, _tools[], _plan[], _confirms[], _tasks{}. `content` holds streamed text.
 */
export function applyAgentEvent(msg, event, p) {
  const m = { ...msg, _agent: true };
  const tools = [...(m._tools || [])];
  switch (event) {
    case AGENT_EVENTS.ACK:
      if (!m._gotText && p?.ack) m.content = "⚡ " + p.ack;
      break;
    case AGENT_EVENTS.DELTA:
      m.content = (m._gotText ? (m.content || "") : "") + (p?.text || "");
      m._gotText = true;
      break;
    case "replace":
      if (p?.text) { m.content = p.text; m._gotText = true; }
      break;
    case AGENT_EVENTS.PLAN:
      m._plan = Array.isArray(p?.steps) ? p.steps : m._plan;
      break;
    case AGENT_EVENTS.TOOL_START: {
      const i = tools.findIndex(t => t.call_id === p?.call_id);
      const t = { call_id: p?.call_id, name: p?.name, tier: p?.tier, status: "running" };
      if (i >= 0) tools[i] = { ...tools[i], ...t }; else tools.push(t);
      m._tools = tools;
      break;
    }
    case AGENT_EVENTS.UI_ACTION: {
      if (!tools.some(t => t.call_id === p?.call_id)) tools.push({ call_id: p?.call_id, name: p?.name, tier: "ui", status: "running" });
      m._tools = tools;
      break;
    }
    case "ui_result":
    case AGENT_EVENTS.TOOL_DONE: {
      const i = tools.findIndex(t => t.call_id === p?.call_id);
      const upd = { status: p?.ok === false ? "error" : "ok", ok: p?.ok !== false };
      if (p?.ms != null) upd.ms = p.ms;
      if (p?.summary) upd.summary = String(p.summary).slice(0, 200);
      if (p?.error) upd.summary = String(p.error).slice(0, 200);
      if (i >= 0) tools[i] = { ...tools[i], ...upd };
      else tools.push({ call_id: p?.call_id, name: p?.name, ...upd });
      m._tools = tools;
      break;
    }
    case AGENT_EVENTS.CONFIRM: {
      const confirms = [...(m._confirms || [])].filter(c => c.call_id !== p?.call_id);
      confirms.push({ call_id: p?.call_id, name: p?.name, input: p?.input, summary: p?.summary, status: "pending" });
      m._confirms = confirms;
      break;
    }
    case AGENT_EVENTS.TASK: {
      m._tasks = { ...(m._tasks || {}), [p?.task_id || "task"]: { status: p?.status, message: p?.message } };
      break;
    }
    case AGENT_EVENTS.DONE:
      if (typeof p?.text === "string" && p.text.trim()) m.content = p.text;
      else if (!m._gotText) m.content = m.content && !String(m.content).startsWith("⚡") && !String(m.content).startsWith("⏳") ? m.content : "Done.";
      m._streaming = false;
      if (p?.ms != null) m._ms = p.ms;
      break;
    case AGENT_EVENTS.ERROR:
      m.content = (m._gotText ? (m.content || "") + "\n" : "") + "❌ " + (p?.message || p?.error || "error");
      m._streaming = false;
      break;
    default:
      break;
  }
  return m;
}

/** Update one confirm card inside a message (by call_id). */
export function setConfirmStatus(msg, callId, status, extra = {}) {
  if (!msg?._confirms) return msg;
  return { ...msg, _confirms: msg._confirms.map(c => c.call_id === callId ? { ...c, status, ...extra } : c) };
}

/**
 * Small hook for self-contained chat columns (DualChatPanel). Holds its own messages.
 */
export function useAgentChat({ apiFetch, agent, getContext, runUiAction, initialMessages = [] }) {
  const [messages, setMessages] = useState(initialMessages);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef(null);
  const msgsRef = useRef(messages);
  msgsRef.current = messages;

  const patchLast = (fn) => setMessages(ms => {
    const out = ms.slice();
    for (let i = out.length - 1; i >= 0; i--) if (out[i]._streaming) { out[i] = fn(out[i]); return out; }
    return ms;
  });

  const send = useCallback(async (text) => {
    const t = String(text || "").trim();
    if (!t || busy) return;
    const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const history = msgsRef.current
      .filter(m => (m.role === "user" || m.role === "assistant") && !m._isToolBadge && m.content)
      .slice(-16)
      .map(m => ({ role: m.role, content: String(m.content).slice(0, 1200) }));
    setMessages(ms => [...ms, { role: "user", content: t, time }, { role: "assistant", content: "⏳ …", time, _streaming: true, _agent: true }]);
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await streamAgentChat({
        apiFetch, agent, signal: ctrl.signal, runUiAction,
        messages: [...history, { role: "user", content: t }],
        context: typeof getContext === "function" ? getContext() : (getContext || {}),
        onEvent: (ev, p) => patchLast(m => applyAgentEvent(m, ev, p)),
      });
      patchLast(m => ({ ...m, _streaming: false }));
    } catch (e) {
      if (e?.name !== "AbortError") patchLast(m => applyAgentEvent(m, AGENT_EVENTS.ERROR, { message: e?.message || String(e) }));
      else patchLast(m => ({ ...m, _streaming: false }));
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }, [apiFetch, agent, getContext, runUiAction, busy]);

  const confirm = useCallback(async (callId, approve) => {
    setMessages(ms => ms.map(m => setConfirmStatus(m, callId, "sending")));
    try {
      const res = await confirmAgentCall(apiFetch, callId, approve);
      const summary = res?.summary || res?.message || res?.text || (approve ? "Done." : "Cancelled.");
      setMessages(ms => ms.map(m => setConfirmStatus(m, callId, approve ? "approved" : "cancelled", { result: String(summary).slice(0, 300) })));
    } catch (e) {
      setMessages(ms => ms.map(m => setConfirmStatus(m, callId, "error", { result: e.message })));
    }
  }, [apiFetch]);

  const abort = useCallback(() => abortRef.current?.abort(), []);
  return { messages, setMessages, busy, send, confirm, abort };
}
